import '../../../scss/SpinsIntelligence.scss';
import Page from "../../Page.jsx";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, Checkbox, CircularProgress} from "@mui/material";
import {useFetchSubscribedUsers} from "./SpinsIntelligence.hooks.jsx";
import {ComboBox} from "../../comboBox/ComboBox.jsx";
import {Table} from "../../tables/Table.jsx";
import {filterUserGroups, sortBoolean, sortUserGroups} from "./utils.js";
import * as thisModule from './SpinsIntelligence.jsx';

const options = [
    'Collection PowerTabs',
    'SPINS Strategic Partners Team',
    'SPINS Product Team Core',
    'Collection PowerTabs Demo',
    'SPINS Client Insights Team',
    'SPINS Customer Success Team',
    'SPINS Client Insights Transition Task Force',
    'SPINS Data QA Team',
    'IQVIA',
    'Client Beta',
]

export const UserGroupCombobox = ({
                                      params,
                                      onUserGroupChange,
                                  }) => {
    const combinedOptions = useMemo(() => [...(new Set(
        [
            ...options,
            ...(params?.rows?.flatMap(t => t.original.userGroups ?? []).map(t => t?.value))
        ]
    ))].map(t => ({
        displayName: t,
        value: t
    })), [params.rows]);

    return (
        <ComboBox
            style={{width: '100%'}}
            value={params?.row?.values?.userGroups ?? []}
            isMultiple={true}
            disableCloseOnSelect={true}
            data={combinedOptions}
            placeholder={"Add group..."}
            freeSolo
            onChange={(event, value) => {
                onUserGroupChange(
                    Number(params?.row?.id),
                    value.map((option) =>
                        typeof option === 'string'
                            ? {displayName: option, value: option}
                            : option
                    )
                )
            }}
        />
    )
}

export const ResetUserGroupsButton = ({params, onResetUserGroups}) => {
    const [isResetting, setIsResetting] = useState(false)

    return (
        <Button
            variant="text"
            style={{textTransform: "none"}}
            data-testid={"reset-user-groups-button"}
            disabled={isResetting}
            startIcon={isResetting ? <CircularProgress size={14}/> : null}
            onClick={() => {
                setIsResetting(true)
                Promise.resolve(
                    onResetUserGroups(
                        Number(params?.row?.id),
                        params?.row?.original?.userSalesforceId,
                        params?.row?.original?.companyId,
                    )
                ).finally(() => setIsResetting(false))
            }}
        >
            Reset User Groups
        </Button>
    )
}

const UserDetailCheckbox = ({params, field, onToggle}) => {
    return (
        <Checkbox
            checked={Boolean(params?.row?.values?.[field])}
            onChange={(e) => {
                onToggle(Number(params?.row?.id), field, e.target.checked);
            }}
        />
    );
}

const SelectColumnFilter = ({
                                column: {filterValue, setFilter},
                            }) => {
    return (
        <select
            value={filterValue}
            onChange={e => {
                setFilter(e.target.value || undefined);
            }}
            style={{
                maxWidth: "50px",
                height: "24px",
                border: "1px solid",
                borderRadius: 4,
                padding: "0 2px",
                cursor: "pointer",
                width: "100%",
                outline: "none",
                textAlign: "center"
            }}>
            >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
        </select>
    );
}

const COLUMNS = ({onCheckboxToggle, onUserGroupChange, onResetUserGroups}) => [
    {
        accessor: 'userName',
        id: 'userName',
        Header: 'Name',
        flex: 1
    }, {
        accessor: 'userEmail',
        id: 'userEmail',
        Header: 'Email',
        flex: 1
    }, {
        accessor: 'companyName',
        id: 'companyName',
        Header: 'Company Name',
        flex: 1
    }, {
        accessor: 'companyType',
        id: 'companyType',
        Header: 'Company Type',
        flex: 1
    }, {
        accessor: 'companyClientSegment',
        id: 'companyClientSegment',
        Header: 'Client Segment',
        flex: 1
    }, {
        accessor: 'isAdmin',
        id: 'isAdmin',
        Header: 'Admin',
        flex: 1,
        Cell: (params) => (
            <UserDetailCheckbox
                params={params}
                field="isAdmin"
                onToggle={onCheckboxToggle}
            />
        ),
        Filter: SelectColumnFilter,
        sortType: sortBoolean,
    }, {
        accessor: 'userGroups',
        id: 'userGroups',
        Header: 'User Groups',
        flex: 2,
        valueGetter: (value) => value?.row?.userGroups?.map(u => u.value).join(','),
        Cell: (params) => {
            return (
                <thisModule.UserGroupCombobox
                    params={params}
                    onUserGroupChange={onUserGroupChange}
                />
            )
        },
        filter: filterUserGroups,
        sortType: sortUserGroups,
    }, {
        id: 'resetUserGroups',
        Header: 'Click to reset user groups to Default',
        flex: 1,
        disableFilters: true,
        disableSortBy: true,
        Cell: (params) => {
            return (
                <thisModule.ResetUserGroupsButton
                    params={params}
                    onResetUserGroups={onResetUserGroups}
                />
            )
        },
    },
]

const SaveHeader = ({saveDisabled, onClick}) => {
    return (
        <Box sx={{p: 1, pb: 0,}}
             style={{display: "flex", justifyContent: "end", paddingBottom: "10px", borderBottom: "1px solid gray"}}>
            <Button
                style={{display: "flex", marginRight: "5px", height: "33px"}}
                variant="contained"
                data-testid={"save-button"}
                onClick={onClick}
                disabled={saveDisabled}
            >
                Save Changes
            </Button>
        </Box>
    )
}

const hasTrackedChanges = (initialUsers, currentUsers) => {
    const TRACKED_FIELDS = ['isAdmin', 'userGroups']

    if (initialUsers.length !== currentUsers.length) {
        return true
    }

    return currentUsers.some((user, index) => {
        const initialUser = initialUsers[index]

        if (!initialUser || initialUser.id !== user.id) {
            return true
        }

        return TRACKED_FIELDS.some((field) =>
            JSON.stringify(initialUser[field] ?? []) !== JSON.stringify(user[field] ?? [])
        )
    })
}

export const SpinsIntelligence = () => {
    const {
        subscribedUsers,
        loading,
        setSubscribedUsers,
        fetchSubscribedUsers,
        fetchDefaultUserGroups,
        upsertUsers
    } = useFetchSubscribedUsers()
    const [initialSubscribedUsers, setInitialSubscribedUsers] = useState([])
    const [saveDisabled, setSaveDisabled] = useState(true)
    const [skipAutoReset, setSkipAutoReset] = useState(false)

    const getUpdatedUsers = () => {
        const filtered = subscribedUsers.filter((sub) => {
            const original = initialSubscribedUsers.find((u) => u.id === sub.id);

            return JSON.stringify(original) !== JSON.stringify(sub);
        })

        return filtered.map((user) => ({
            ...user,
            userGroups: user.userGroups.map((group) => group.value),
        }))
    }

    useEffect(() => {
        fetchSubscribedUsers()
    }, []);

    useEffect(() => {
        if (subscribedUsers.length > 0 && initialSubscribedUsers.length === 0) {
            setInitialSubscribedUsers(subscribedUsers)
        }
    }, [subscribedUsers, initialSubscribedUsers.length])

    const handleCheckboxToggle = useCallback((id, field, checked) => {
        setSkipAutoReset(true)
        setSubscribedUsers((prevUsers) => {
            const updatedUsers = prevUsers.map((user) =>
                user.id === id
                    ? {
                        ...user,
                        [field]: checked,
                    }
                    : user
            )

            setSaveDisabled(!hasTrackedChanges(initialSubscribedUsers, updatedUsers))

            return updatedUsers
        })
    }, [initialSubscribedUsers])

    const handleUserGroupChange = useCallback((id, value) => {
        setSkipAutoReset(true)
        setSubscribedUsers((prevUsers) => {
            const updatedUsers = prevUsers.map((user) =>
                user.id === id
                    ? {
                        ...user,
                        userGroups: value,
                    }
                    : user
            )

            setSaveDisabled(!hasTrackedChanges(initialSubscribedUsers, updatedUsers))

            return updatedUsers
        })
    }, [initialSubscribedUsers])

    const handleResetUserGroups = useCallback(async (id, userSalesforceId, companyId) => {
        const defaultUserGroups = await fetchDefaultUserGroups(userSalesforceId, companyId)

        handleUserGroupChange(id, defaultUserGroups)
    }, [fetchDefaultUserGroups, handleUserGroupChange])

    const columns = useMemo(() => {
        return COLUMNS({
            onCheckboxToggle: handleCheckboxToggle,
            onUserGroupChange: handleUserGroupChange,
            onResetUserGroups: handleResetUserGroups,
        })
    }, [handleCheckboxToggle, handleUserGroupChange, handleResetUserGroups])

    useEffect(() => {
        setSkipAutoReset(false)
    }, [subscribedUsers])

    return (
        <>
            <Page name={'Spins Intelligence'}>
                {loading ?
                    <CircularProgress/> :
                    <>
                        <SaveHeader saveDisabled={saveDisabled} onClick={() => upsertUsers(getUpdatedUsers())}/>
                        <Table data={subscribedUsers ?? []} columns={columns} skipAutoReset={skipAutoReset}/>
                    </>
                }
            </Page>
        </>
    )
}